import { cn } from '@/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/**
 * RDS Loader — Primary-600(#4152CF) 원호 스피너.
 * @see https://www.figma.com/design/wcgji99mZRPIytCZDjwqxe/RDS-v1.0?node-id=104-1634
 */
const loaderSizeConfig = {
  giant: {
    className: 'size-14',
    viewBox: '0 0 62 62',
    strokeWidth: 6,
    d: 'M3 31C3 15.536 15.536 3 31 3C46.464 3 59 15.536 59 31C59 46.464 46.464 59 31 59C25.6922 59 20.7294 57.5231 16.5 54.9579',
  },
  large: {
    className: 'size-12',
    viewBox: '0 0 54 54',
    strokeWidth: 6,
    d: 'M3 27C3 13.7452 13.7452 3 27 3C40.2548 3 51 13.7452 51 27C51 40.2548 40.2548 51 27 51C22.4505 51 18.1966 49.7341 14.5714 47.5353',
  },
  medium: {
    className: 'size-10',
    viewBox: '0 0 45 45',
    strokeWidth: 5,
    d: 'M2.5 22.5C2.5 11.4543 11.4543 2.5 22.5 2.5C33.5457 2.5 42.5 11.4543 42.5 22.5C42.5 33.5457 33.5457 42.5 22.5 42.5C18.7087 42.5 15.1638 41.4451 12.1429 39.6128',
  },
  small: {
    className: 'size-8',
    viewBox: '0 0 36 36',
    strokeWidth: 4,
    d: 'M2 18C2 9.16344 9.16344 2 18 2C26.8366 2 34 9.16344 34 18C34 26.8366 26.8366 34 18 34C14.967 34 12.1311 33.1561 9.71429 31.6902',
  },
  tiny: {
    className: 'size-6',
    viewBox: '0 0 28 28',
    strokeWidth: 4,
    d: 'M2 14C2 7.37258 7.37258 2 14 2C20.6274 2 26 7.37258 26 14C26 20.6274 20.6274 26 14 26C11.7252 26 9.5983 25.3671 7.78571 24.2677',
  },
} as const;

export const loaderVariants = cva('inline-block shrink-0 animate-spin text-ruda-primary-600', {
  variants: {
    size: {
      giant: loaderSizeConfig.giant.className,
      large: loaderSizeConfig.large.className,
      medium: loaderSizeConfig.medium.className,
      small: loaderSizeConfig.small.className,
      tiny: loaderSizeConfig.tiny.className,
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

export interface LoaderProps
  extends Omit<React.SVGAttributes<SVGSVGElement>, 'children'>,
    VariantProps<typeof loaderVariants> {}

const Loader = React.forwardRef<SVGSVGElement, LoaderProps>(
  ({ className, size = 'medium', ...props }, ref) => {
    const config = loaderSizeConfig[size ?? 'medium'];

    return (
      <svg
        ref={ref}
        viewBox={config.viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        className={cn(loaderVariants({ size }), className)}
        {...props}
      >
        <path
          d={config.d}
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
        />
      </svg>
    );
  },
);
Loader.displayName = 'Loader';

export { Loader };
